import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  VStack,
  HStack,
  Heading,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Alert,
  AlertIcon,
  useToast,
  TableContainer,
  Select,
  Text,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon, ViewIcon, RepeatIcon } from "@chakra-ui/icons";
import { adminApiService } from "../services/adminApiService";
import { JobLevel, JobLevelCreate, JobLevelUpdate, QueryParams } from "../types";

const JobLevelManagement: React.FC = () => {
  const [jobLevels, setJobLevels] = useState<JobLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedJobLevel, setSelectedJobLevel] = useState<JobLevel | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formData, setFormData] = useState<JobLevelCreate>({
    name: "",
    odoo_id: undefined,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const toast = useToast();

  // Fetch job levels data
  const fetchJobLevels = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: QueryParams = {
        search: searchTerm || undefined,
        include_disabled: includeDisabled,
      };
      const response = await adminApiService.getJobLevels(params);
      setJobLevels(response.data);
    } catch (error: any) {
      setError("Failed to fetch job levels");
      console.error("Error fetching job levels:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobLevels();
  }, [searchTerm, includeDisabled]);

  const handleOpenModal = (jobLevel?: JobLevel) => {
    if (jobLevel) {
      setSelectedJobLevel(jobLevel);
      setFormData({
        name: jobLevel.name,
        odoo_id: jobLevel.odoo_id,
      });
      setIsEditing(true);
    } else {
      setSelectedJobLevel(null);
      setFormData({
        name: "",
        odoo_id: undefined,
      });
      setIsEditing(false);
    }
    onOpen();
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast({
        title: "Validation Error",
        description: "Name is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSubmitLoading(true);
    try {
      if (isEditing && selectedJobLevel) {
        const updateData: JobLevelUpdate = {
          name: formData.name,
          odoo_id: formData.odoo_id,
        };
        await adminApiService.updateJobLevel(selectedJobLevel.id, updateData);
        toast({
          title: "Success",
          description: "Job level updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        await adminApiService.createJobLevel(formData);
        toast({
          title: "Success",
          description: "Job level created successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
      onClose();
      fetchJobLevels();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to ${isEditing ? "update" : "create"} job level: ${error.response?.data?.detail || error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error submitting job level:", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (jobLevel: JobLevel) => {
    try {
      await adminApiService.deleteJobLevel(jobLevel.id);
      toast({
        title: "Success",
        description: "Job level deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchJobLevels();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to delete job level: ${error.response?.data?.detail || error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error deleting job level:", error);
    }
  };

  const handleRestore = async (jobLevel: JobLevel) => {
    try {
      await adminApiService.restoreJobLevel(jobLevel.id);
      toast({
        title: "Success",
        description: "Job level restored successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchJobLevels();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to restore job level: ${error.response?.data?.detail || error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error restoring job level:", error);
    }
  };

  if (loading) {
    return (
      <Box p={6} textAlign="center">
        <Text>Loading job levels...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg" mb={4}>Job Level Management</Heading>
          
          <HStack spacing={4} mb={4}>
            <Input
              placeholder="Search job levels..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              maxW="300px"
            />
            <Select
              value={includeDisabled ? "all" : "active"}
              onChange={(e) => setIncludeDisabled(e.target.value === "all")}
              maxW="200px"
            >
              <option value="active">Active Only</option>
              <option value="all">Include Disabled</option>
            </Select>
            <Button colorScheme="blue" onClick={() => handleOpenModal()}>
              Add Job Level
            </Button>
          </HStack>
        </Box>

        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Job Level Name</Th>
                <Th>Odoo ID</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {jobLevels.map((jobLevel) => (
                <Tr key={jobLevel.id}>
                  <Td>{jobLevel.name}</Td>
                  <Td>
                    {jobLevel.odoo_id ? (
                      <Badge colorScheme="blue" variant="outline">
                        {jobLevel.odoo_id}
                      </Badge>
                    ) : (
                      <Text color="gray.500">-</Text>
                    )}
                  </Td>
                  <Td>
                    <Badge colorScheme={jobLevel.is_disabled ? "red" : "green"}>
                      {jobLevel.is_disabled ? "Disabled" : "Active"}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <IconButton
                        aria-label="View"
                        icon={<ViewIcon />}
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenModal(jobLevel)}
                      />
                      {!jobLevel.is_disabled ? (
                        <>
                          <IconButton
                            aria-label="Edit"
                            icon={<EditIcon />}
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            onClick={() => handleOpenModal(jobLevel)}
                          />
                          <IconButton
                            aria-label="Delete"
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            onClick={() => handleDelete(jobLevel)}
                          />
                        </>
                      ) : (
                        <IconButton
                          aria-label="Restore"
                          icon={<RepeatIcon />}
                          size="sm"
                          colorScheme="green"
                          variant="outline"
                          onClick={() => handleRestore(jobLevel)}
                        />
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>

        {jobLevels.length === 0 && (
          <Alert status="info">
            <AlertIcon />
            No job levels found matching your criteria.
          </Alert>
        )}
      </VStack>

      {/* Job Level Form Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isEditing ? "Edit Job Level" : "Add New Job Level"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Job Level Name</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter job level name"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Odoo ID</FormLabel>
                <Input
                  type="number"
                  value={formData.odoo_id || ""}
                  onChange={(e) => setFormData({...formData, odoo_id: e.target.value ? parseInt(e.target.value) : undefined})}
                  placeholder="Enter Odoo ID (optional)"
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleSubmit}
              isLoading={submitLoading}
              loadingText={isEditing ? "Updating..." : "Creating..."}
            >
              {isEditing ? "Update" : "Create"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default JobLevelManagement;
